/**
 * Hash → route id resolution (longest manifest / alias hash wins).
 * Keeps first-segment fallback for unlisted paths (#/devices/:id).
 */

/**
 * @param {string} hashFull hash path without query (e.g. "#/account/cameras")
 * @param {{
 *   routes?: ReadonlyArray<{ id: string, hash: string }>,
 *   aliases?: Record<string, string>,
 * }} [opts]
 * @returns {{ id: string, args: string[] }}
 */
export function resolveHashRoute(hashFull, opts = {}) {
  const path = String(hashFull || "").trim() || "#/overview";
  const routeList = Array.isArray(opts.routes) ? opts.routes : [];
  const aliases = opts.aliases && typeof opts.aliases === "object" ? opts.aliases : {};

  /** @type {Array<{ id: string, args: string[], len: number }>} */
  const candidates = [];

  const pushExactOrPrefix = (h, id) => {
    const hash = String(h || "");
    const rid = String(id || "").trim();
    if (!hash || !rid) return;
    if (path === hash) {
      candidates.push({ id: rid, args: [], len: hash.length });
      return;
    }
    if (path.startsWith(hash + "/")) {
      const rest = path
        .slice(hash.length + 1)
        .split("/")
        .filter(Boolean)
        .map((p) => {
          try {
            return decodeURIComponent(p);
          } catch (_) {
            return p;
          }
        });
      candidates.push({ id: rid, args: rest, len: hash.length });
    }
  };

  for (const r of routeList) {
    if (!r) continue;
    pushExactOrPrefix(r.hash, r.id);
  }
  for (const [alias, rid] of Object.entries(aliases)) {
    pushExactOrPrefix(alias, rid);
  }

  if (candidates.length) {
    candidates.sort((a, b) => b.len - a.len || a.id.localeCompare(b.id));
    return { id: candidates[0].id, args: candidates[0].args };
  }

  const parts = path.split("/");
  const rawId = parts[1] || "overview";
  const args = parts.slice(2).map((p) => {
    try {
      return decodeURIComponent(p);
    } catch (_) {
      return p;
    }
  });
  return { id: rawId, args };
}
